import { useEffect, useState, useRef } from "react";
import { Linkedin } from "lucide-react";
import { leadership } from "../data/mock";

const LeadershipSection = () => {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="leadership" className="py-32 px-6" ref={sectionRef}>
      <div className="max-w-6xl mx-auto">
        {/* Section Title */}
        <div
          className={`text-center mb-16 transform transition-all duration-700 ${
            visible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
          }`}
        >
          <h2 className="font-mono text-3xl md:text-4xl text-[#F3F4F6] tracking-wide">
            Leadership
          </h2>
          <div className="w-24 h-[1px] bg-gradient-to-r from-transparent via-[#00F0FF]/50 to-transparent mx-auto mt-6" />
        </div>

        {/* Leadership Card */}
        <div
          className={`relative p-8 md:p-12 rounded-2xl backdrop-blur-xl bg-white/[0.03] border border-white/10 transform transition-all duration-700 delay-150 ${
            visible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
          }`}
        >
          <div className="grid lg:grid-cols-[1fr,320px] gap-12 items-start">
            {/* Content */}
            <div>
              {/* Role Tag */}
              <span className="inline-block font-mono text-xs tracking-[0.2em] text-[#00F0FF] border border-[#00F0FF]/30 px-3 py-1 rounded-sm mb-4">
                {leadership.role}
              </span>

              {/* Name */}
              <h3 className="font-mono text-2xl md:text-3xl text-[#F3F4F6] tracking-wide mb-2">
                {leadership.name}
              </h3>
              <span className="font-sans text-[#6B7280] text-lg">
                {leadership.title}
              </span>

              {/* Bio Paragraphs */}
              <div className="mt-8 space-y-6">
                {leadership.bio.map((paragraph, idx) => (
                  <p
                    key={idx}
                    className="font-sans text-[#F3F4F6]/80 text-base leading-relaxed"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>

              {/* Social Links */}
              <div className="flex gap-4 mt-8">
                <a
                  href={leadership.socialLinks.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-[#6B7280] hover:text-[#00F0FF] hover:border-[#00F0FF]/30 transition-all duration-300"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Image */}
            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden border border-white/10">
                <img
                  src={leadership.image}
                  alt={leadership.name}
                  className="w-full h-full object-cover object-center"
                />
              </div>
              {/* Decorative glow */}
              <div className="absolute -inset-4 bg-gradient-to-br from-[#00F0FF]/10 via-transparent to-orange-500/10 rounded-3xl -z-10 blur-xl opacity-50" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LeadershipSection;
